import { useNavigation } from '@react-navigation/native'
import React from 'react'

import { Avatar } from 'widgets/Avatar'
import { defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { FilledButton } from 'widgets/Button/FilledButton'
import { Icon } from 'widgets/Icon'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'
import { Text } from 'widgets/Text'

import { useGroup } from 'hooks/useGroup'
import { useMembershipRequest } from 'hooks/useMembershipRequest'
import { useSubscribed } from 'hooks/useSubscribed'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { getTextForNotificationType } from 'utils/notifications/getTextForNotificationType'
import { timeSince } from 'utils/time'

import { useFocusMode } from 'contexts/FocusModeContext'

import { PendingMembership } from 'types/pendingMemberships'

type RequestRowProps = {
  pendingMembership: PendingMembership
}

export const RequestRow = ({ pendingMembership }: RequestRowProps) => {
  const navigation = useNavigation<any>()
  const { data: user } = useUser({ userId: pendingMembership.userId })
  const { data: group } = useGroup({ groupId: pendingMembership.program.id })
  const { data: programTrainer } = useUser({ userId: pendingMembership?.program?.trainer.id })
  const { acceptMembershipRequest, declineMembershipRequest, isLoading } = useMembershipRequest({
    userId: pendingMembership.userId,
    programId: pendingMembership.program.id,
  })

  const focusMode = useFocusMode()
  const { isSubscribed } = useSubscribed({ programId: group?.id })

  const navigateToUpspace = (username: string | undefined) => {
    if (isSubscribed) {
      focusMode.setGroup(group)
      return
    }
    navigation.navigate(links.GROUP_STACK, {
      screen: links.GROUP_PAGE,
      params: { username },
    })
  }

  const navigateToProfile = (username: string | undefined) => {
    navigation.navigate(links.PROFILE_STACK, {
      username,
    })
  }

  const avatar = user?.avatar ?? defaultAvatarImage
  const username = user?.username
  const { text } = getTextForNotificationType('request')
  const programTitle = group?.title
  const timestamp = timeSince(pendingMembership.updated)

  const handleRequestAccept = () => {
    acceptMembershipRequest.mutateAsync()
  }

  const handleRequestDecline = () => {
    declineMembershipRequest.mutateAsync()
  }

  const renderRowActions = () => {
    return (
      <Box flexDirection='row' margin={{ vertical: 'sm' }}>
        {isLoading ? (
          <LoadingSpinner size='small' />
        ) : (
          <Box flexDirection='row'>
            <FilledButton title='Accept' height={28} fontSize='sm' onPress={handleRequestAccept} />
            <Box margin={{ left: 'sm' }}>
              <Pressable onPress={handleRequestDecline}>
                <Icon color='black' name='x' />
              </Pressable>
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Pressable disabled>
      <Box height={60} flexDirection='row' align='center' margin={{ vertical: 'xs' }}>
        <Box>
          <Pressable onPress={() => navigateToProfile(username)}>
            <Avatar height={44} width={44} image={avatar} />
          </Pressable>
        </Box>

        <Box flex={1} margin={{ horizontal: 'smd' }}>
          <Text color='greyDarkest' numberOfLines={2} ellipsizeMode='tail'>
            <PressableText color='black' bold onPress={() => navigateToProfile(username)}>
              {username}
            </PressableText>
            {` ${text}`}
            <PressableText
              disabled={programTitle === undefined}
              onPress={() => navigateToUpspace(programTrainer?.username)}
              color={programTitle !== undefined ? 'black' : 'greyDarkest'}
              bold={programTitle !== undefined}
            >
              {` ${programTitle ?? 'your program'}`}
            </PressableText>
            <Text fontSize='sm' numberOfLines={1} color='greyDarker'>{`  ${timestamp}`}</Text>
          </Text>
        </Box>

        <Box flexDirection='row'>{renderRowActions()}</Box>
      </Box>
    </Pressable>
  )
}
